
import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import { STU_KYC_NEXT_STEP } from '@/constants/superTopupConfig';
import { STU_SESSION_KEYS } from '@/constants/stuQueryKeys';
import { IStuJourney, TStuJourneyStage } from '@/types/stu';
import { formatSumInsured } from '@/utils/stu/plans';

/** Every page of the super top-up journey lives under this prefix; nothing here knows another. */
export const STU_BASE_PATH = '/retail/super-top-up';

export const STU_ERROR_PATH = `${STU_BASE_PATH}/error`;

export const STU_NEW_QUOTE_PATH = `${STU_BASE_PATH}/select-member`;

/**
 * The plan step: deductible, cover, tenure, the option and its add-ons on one screen. There is no
 * per-plan page: the step remembers the option by product code, so "back to the plan" is the step.
 */
export const STU_PLANS_PATH = `${STU_BASE_PATH}/plans`;

export const STU_BENEFITS_PATH = `${STU_BASE_PATH}/benefits`;

/** Where a call-back request lands: the consultant will call. */
export const STU_THANK_YOU_PATH = `${STU_BASE_PATH}/thank-you`;

/** The super top-up screens. Payment returns (`success`/`failure`) sit outside the journey layout. */
export const stuPaths = {
  home: STU_BASE_PATH,
  newQuote: STU_NEW_QUOTE_PATH,
  plans: STU_PLANS_PATH,
  error: STU_ERROR_PATH,
  benefits: STU_BENEFITS_PATH,
  thankYou: STU_THANK_YOU_PATH,
  proposal: (planId: string | number) => `${STU_BASE_PATH}/proposal/${planId}`,
  proposalReview: (planId: string | number) => `${STU_BASE_PATH}/proposal/${planId}/review`,
  payment: (planId: string | number, quoteId?: string) =>
    `${STU_BASE_PATH}/payment/${planId}${quoteId ? `?quoteId=${encodeURIComponent(quoteId)}` : ''}`,
  policy: (planId: string | number, quoteId: string) =>
    `${STU_BASE_PATH}/policy/${planId}?quoteId=${quoteId}`,
  success: (planId: string | number, quoteId: string) =>
    `${STU_BASE_PATH}/success?quoteId=${quoteId}&planId=${planId}`,
};

export const stuKycPath = (planId: string | number, quoteId: string) =>
  `${STU_BASE_PATH}/kyc/${planId}?quoteId=${encodeURIComponent(quoteId)}`;

/**
 * The screen a committed plan goes to, from `kyc.nextStep` alone. The KYC page shows the form,
 * OTP, processing or price step from its own state. Anything else (no block, or a step this
 * build doesn't know) goes to the KYC page too, which asks the server again; never straight
 * to the proposal.
 */
export const stuNextStepPath = (
  nextStep: string | undefined,
  planId: string,
  quoteId: string,
) =>
  nextStep === STU_KYC_NEXT_STEP.PROPOSAL
    ? stuPaths.proposal(planId)
    : stuKycPath(planId, quoteId);

export type TStuJourneyTarget =
  | 'plans'
  | 'planSelection'
  | 'kyc'
  | 'proposal'
  | 'proposalReview'
  | 'payment'
  | 'paymentStatus'
  | 'policy'
  | 'support'
  | 'newQuote';

interface IStuJourneyStageConfig {
  title: string;
  cta: string;
  target: TStuJourneyTarget;
}

const CONTINUE = 'Continue';

export const STU_JOURNEY_STAGES: Record<
  TStuJourneyStage,
  IStuJourneyStageConfig
> = {
  POLICY_ISSUED: {
    title: 'Policy Issued',
    cta: 'View',
    target: 'policy',
  },
  POLICY_IN_PROGRESS: {
    title: 'Policy is being issued',
    cta: 'View',
    target: 'paymentStatus',
  },
  POLICY_ATTENTION: {
    title: 'This policy needs attention',
    cta: 'Chat with us',
    target: 'support',
  },
  PAYMENT_SUCCEEDED: {
    title: 'Payment successful',
    cta: 'View',
    target: 'paymentStatus',
  },
  PAYMENT_IN_PROGRESS: {
    title: 'A payment is already in progress',
    cta: CONTINUE,
    target: 'paymentStatus',
  },
  PAYMENT_UNCONFIRMED: {
    title: "We're confirming your payment.",
    cta: CONTINUE,
    target: 'paymentStatus',
  },
  PAYMENT_FAILED: {
    title: 'Payment failed',
    cta: CONTINUE,
    target: 'payment',
  },
  PROPOSAL_SUBMITTED: {
    title: 'Proposal submitted',
    cta: CONTINUE,
    target: 'payment',
  },
  PROPOSAL_AFFIRMED: {
    title: "You've confirmed these details",
    cta: CONTINUE,
    target: 'proposalReview',
  },
  PROPOSAL_AWAITING_CUSTOMER: {
    title: 'Action needed',
    cta: CONTINUE,
    target: 'proposalReview',
  },
  PROPOSAL_IN_PROGRESS: {
    title: 'This application has not been submitted yet',
    cta: CONTINUE,
    target: 'proposal',
  },
  KYC_OTP_REQUIRED: {
    title: 'Enter the code we sent you',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_IN_PROGRESS: {
    title: 'Verifying your identity',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_PRICE_PENDING: {
    title: 'The premium for this plan has changed',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_FAILED: {
    title: 'We couldn’t verify you with this insurer',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_REQUIRED: {
    title: 'Complete your KYC to continue',
    cta: CONTINUE,
    target: 'kyc',
  },
  PLAN_COMMITTED: {
    title: 'Plan selected',
    cta: CONTINUE,
    target: 'proposal',
  },
  PLAN_CONFIGURING: {
    title: 'Finish setting up your plan',
    cta: CONTINUE,
    target: 'planSelection',
  },
  PLAN_LIST: {
    title: 'Choose a plan',
    cta: CONTINUE,
    target: 'plans',
  },
  QUOTE_IN_PROGRESS: {
    title: 'Fetching plans for you',
    cta: CONTINUE,
    target: 'plans',
  },
  QUOTE_UNAVAILABLE: {
    title: 'This quote is no longer available',
    cta: 'Start again',
    target: 'newQuote',
  },
};

const RESELECTION_STAGES: readonly TStuJourneyStage[] = [
  'PLAN_LIST',
  'PLAN_CONFIGURING',
];

export const isKnownJourneyStage = (stage: string): stage is TStuJourneyStage =>
  Object.prototype.hasOwnProperty.call(STU_JOURNEY_STAGES, stage);

const stageConfig = ({ stage }: IStuJourney) =>
  isKnownJourneyStage(stage)
    ? STU_JOURNEY_STAGES[stage]
    : STU_JOURNEY_STAGES.PLAN_LIST;

const isAwaitingReselection = (journey: IStuJourney) =>
  journey.needsReselection && RESELECTION_STAGES.includes(journey.stage);

export const getJourneyStatus = (journey: IStuJourney) => {
  const config = stageConfig(journey);

  return isAwaitingReselection(journey)
    ? { ...config, title: 'Pick your plan again to continue' }
    : config;
};

// Unpaid stages past the plan list; going back from a resume to any of them
// should land on the plan list rather than the home page.
const RETURNS_TO_PLAN_LIST: ReadonlySet<TStuJourneyTarget> = new Set([
  'planSelection',
  'kyc',
  'proposal',
  'proposalReview',
  'payment',
]);

export const getJourneyPath = (journey: IStuJourney): string | null => {
  const { target } = stageConfig(journey);
  const { quoteId, planId } = journey;

  if (target === 'support') return null;
  if (target === 'newQuote') return stuPaths.newQuote;
  if (target === 'plans' || !planId || isAwaitingReselection(journey)) return stuPaths.plans;

  const paths: Record<
    Exclude<TStuJourneyTarget, 'support' | 'newQuote' | 'plans'>,
    string
  > = {
    // The plan step owns the option and its add-ons.
    planSelection: stuPaths.plans,
    kyc: stuKycPath(planId, quoteId),
    proposal: stuPaths.proposal(planId),
    proposalReview: stuPaths.proposalReview(planId),
    payment: stuPaths.payment(planId),
    paymentStatus: stuPaths.success(planId, quoteId),
    policy: stuPaths.policy(planId, quoteId),
  };

  return paths[target];
};

/** Whether backing out of this resumed journey should land on its plan list. */
export const returnsToPlanList = (journey: IStuJourney) =>
  RETURNS_TO_PLAN_LIST.has(stageConfig(journey).target) &&
  getJourneyPath(journey) !== stuPaths.plans;

export const formatJourneyCover = ({ sumInsured }: IStuJourney) => {
  if (sumInsured.unlimited) return 'Unlimited';
  return sumInsured.amount === null ? null : formatSumInsured(sumInsured.amount);
};

/** The plan's name, or a placeholder, never the product code. No advisor label: the journey has no agents. */
export const formatJourneyPlanName = (journey: IStuJourney) =>
  journey.plan?.name ?? 'Super top-up plan';

export const formatJourneySubtitle = (journey: IStuJourney) => {
  const cover = formatJourneyCover(journey);
  return [formatJourneyPlanName(journey), cover ? `${cover} cover` : null]
    .filter(Boolean)
    .join(' · ');
};

export const formatJourneyMembers = ({ members }: IStuJourney) =>
  members
    .map(({ relation }) => {
      const label = relation.replace(/_/g, ' ');
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
    })
    .join(', ');

export const formatJourneyUpdatedAt = ({ lastActivityAt }: IStuJourney) =>
  new Date(lastActivityAt).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });

/** Words the card's expiresAt ("Expires in 3 days"); null, or already past, shows nothing. */
export const formatJourneyExpiry = (
  { expiresAt }: IStuJourney,
  now: Date = new Date(),
): string | null => {
  if (!expiresAt) return null;
  const expires = parseISO(expiresAt);
  if (!isValid(expires) || expires <= now) return null;
  const days = differenceInCalendarDays(expires, now);
  if (days === 0) return 'Expires today';
  return days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`;
};

interface IResumeReturn {
  /** `location.key` of the history entry the resume left from (the home page). */
  key: string;
  /** Where going back to that entry should land instead. */
  to: string;
}

/** Remember that going back to the entry with this key should open `to`. */
export const markResumeReturn = (key: string, to: string) => {
  try {
    const mark: IResumeReturn = { key, to };
    sessionStorage.setItem(STU_SESSION_KEYS.RESUME_RETURN, JSON.stringify(mark));
  } catch {
    // Storage unavailable: back from the resumed page lands on home, as before.
  }
};

const readResumeReturn = (): IResumeReturn | null => {
  try {
    const raw = sessionStorage.getItem(STU_SESSION_KEYS.RESUME_RETURN);
    return raw ? (JSON.parse(raw) as IResumeReturn) : null;
  } catch {
    return null;
  }
};

/** Where the entry with this key should send the customer, if a resume marked it. */
export const resumeReturnFor = (key: string): string | null => {
  const mark = readResumeReturn();
  return mark && mark.key === key ? mark.to : null;
};

export const clearResumeReturn = () => {
  try {
    sessionStorage.removeItem(STU_SESSION_KEYS.RESUME_RETURN);
  } catch {
    // Nothing to clear.
  }
};
